import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Banknote, Plus, Pencil, Trash2, Loader2, Clock, Save,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import RegisterDashboardCard from "./RegisterDashboardCard";
import ZCertEmailSettingsCard from "./ZCertEmailSettingsCard";

const DEFAULT_COLORS = [
  "#8b5cf6", "#f59e0b", "#10b981", "#3b82f6", "#ec4899",
];

// ─── Register dialog ──────────────────────────────────────────────────
function RegisterDialog({ open, onClose, restaurantId, initial, onSaved }) {
  const [name, setName] = useState("");
  const [zone, setZone] = useState("");
  const [color, setColor] = useState(DEFAULT_COLORS[0]);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setZone(initial?.zone || "");
      setColor(initial?.color || DEFAULT_COLORS[0]);
      setActive(initial?.active !== false);
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Ponle un nombre");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: name.trim(), zone: zone.trim(), color, active, sort_order: initial?.sort_order || 0 };
      if (initial?.register_id) {
        await api.updateCashRegister(restaurantId, initial.register_id, payload);
        toast.success(`Caja "${name}" actualizada`);
      } else {
        await api.createCashRegister(restaurantId, payload);
        toast.success(`Caja "${name}" creada`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.message || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="cash-register-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-violet-600" />
            {initial?.register_id ? "Editar caja" : "Nueva caja"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2 text-sm">
          <div>
            <Label className="text-xs">Nombre <span className="text-rose-700">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Barra, Sala, Terraza..." maxLength={40}
              data-testid="cash-register-name" />
          </div>
          <div>
            <Label className="text-xs">Zona (opcional)</Label>
            <Input value={zone} onChange={(e) => setZone(e.target.value)}
              placeholder="Ej. Interior, Planta 1..." maxLength={40} />
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2 mt-1">
              {DEFAULT_COLORS.map(c => (
                <button key={c} type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition ${color === c ? "border-gray-900 scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`cash-register-color-${c.slice(1)}`}
                />
              ))}
            </div>
          </div>
          {initial?.register_id && (
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>Activa (desactivar la oculta de los arqueos diarios sin borrar historial)</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={handleSave} disabled={busy} data-testid="cash-register-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main card ────────────────────────────────────────────────────────
export default function CashRegistersCard({ restaurantId }) {
  const [registers, setRegisters] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReg, setEditingReg] = useState(null);
  const [savingShifts, setSavingShifts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [regs, sh] = await Promise.all([
        api.listCashRegisters(restaurantId),
        api.getShifts(restaurantId),
      ]);
      setRegisters(regs.cash_registers || []);
      setShifts(sh.shifts || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { if (restaurantId) load(); }, [restaurantId, load]);

  const handleDelete = async (reg) => {
    if (!confirm(`¿Borrar la caja "${reg.name}"? Si tiene arqueos históricos se desactivará en vez de borrarse.`)) return;
    try {
      const r = await api.deleteCashRegister(restaurantId, reg.register_id);
      toast.success(r.deactivated ? `"${reg.name}" desactivada (tenía arqueos)` : `"${reg.name}" eliminada`);
      load();
    } catch (e) { toast.error(e?.message); }
  };

  const updateShift = (idx, patch) => {
    setShifts(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const saveShifts = async () => {
    setSavingShifts(true);
    try {
      const payload = shifts.map(s => ({
        shift_id: s.shift_id, name: s.name,
        start_hour: Number(s.start_hour), end_hour: Number(s.end_hour),
      }));
      await api.updateShifts(restaurantId, payload);
      toast.success("Turnos actualizados");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSavingShifts(false); }
  };

  return (
    <Card data-testid="cash-registers-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Banknote className="h-4 w-4 text-violet-600" />
          Cajas y turnos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-center py-3 text-[11px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…
          </div>
        ) : (
          <>
            {/* Registers section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Cajas físicas ({registers.length}/5)
                </div>
                {registers.length < 5 && (
                  <Button size="sm" variant="outline" className="h-7 gap-1"
                    onClick={() => { setEditingReg(null); setDialogOpen(true); }}
                    data-testid="cash-register-create">
                    <Plus className="h-3 w-3" /> Nueva caja
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                {registers.map(reg => (
                  <div key={reg.register_id}
                    className={`flex items-center gap-2 p-2 rounded border ${reg.active ? "bg-card" : "bg-muted/50 opacity-60"}`}
                    data-testid={`cash-register-row-${reg.register_id}`}>
                    <div className="h-6 w-6 rounded-full shrink-0"
                      style={{ backgroundColor: reg.color || "#8b5cf6" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium">{reg.name}</div>
                      {reg.zone && <div className="text-[10px] text-muted-foreground">{reg.zone}</div>}
                      {!reg.active && <div className="text-[10px] text-rose-700 font-medium">Inactiva</div>}
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => { setEditingReg(reg); setDialogOpen(true); }}
                      data-testid={`cash-register-edit-${reg.register_id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-700"
                      onClick={() => handleDelete(reg)}
                      data-testid={`cash-register-delete-${reg.register_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Shifts section */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Turnos ({shifts.length}/5)
                </div>
                <Button size="sm" variant="outline" className="h-7 gap-1"
                  onClick={saveShifts} disabled={savingShifts}
                  data-testid="shifts-save">
                  {savingShifts ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Guardar turnos
                </Button>
              </div>
              <div className="space-y-1">
                {shifts.map((s, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-1.5 items-center text-xs"
                    data-testid={`shift-row-${s.shift_id}`}>
                    <Input className="col-span-5 h-8" value={s.name}
                      onChange={(e) => updateShift(idx, { name: e.target.value })}
                      placeholder="Nombre" maxLength={30} />
                    <Input type="number" className="col-span-3 h-8 tabular-nums text-center"
                      min={0} max={23} value={s.start_hour}
                      onChange={(e) => updateShift(idx, { start_hour: e.target.value })} />
                    <span className="col-span-1 text-center text-muted-foreground">→</span>
                    <Input type="number" className="col-span-3 h-8 tabular-nums text-center"
                      min={0} max={24} value={s.end_hour}
                      onChange={(e) => updateShift(idx, { end_hour: e.target.value })} />
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Los turnos definen ventanas horarias para asignar arqueos. El turno se auto-detecta según la hora de apertura pero puedes cambiarlo manualmente al arquear.
              </p>
            </div>
          </>
        )}
      </CardContent>
      {dialogOpen && (
        <RegisterDialog
          open restaurantId={restaurantId} initial={editingReg}
          onClose={() => { setDialogOpen(false); setEditingReg(null); }}
          onSaved={load}
        />
      )}

      {/* Per-register performance dashboards */}
      {!loading && registers.filter(r => r.active).length > 0 && (
        <div className="mt-4 space-y-3" data-testid="register-dashboards-section">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground px-1">
            Rendimiento por caja (últimos 30 días)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {registers.filter(r => r.active).map(r => (
              <RegisterDashboardCard key={r.register_id}
                restaurantId={restaurantId} register={r} />
            ))}
          </div>
        </div>
      )}

      {/* Z-certificate email settings (push-style fiscal audit trail) */}
      {!loading && (
        <div className="mt-4" data-testid="z-cert-email-section">
          <ZCertEmailSettingsCard restaurantId={restaurantId} />
        </div>
      )}
    </Card>
  );
}
